import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import Spinner from 'components/UI/Spinner';
import { Close } from '@mui/icons-material';
import {
  fileSizeToMb,
} from '../Pricebooks/priceebook.utils';
import { TEXT_LABELS } from '../../constants/productVariantImport';

// Helper function to format file size dynamically
const formatFileSize = (fileSize: number): string => {
  const sizeInMB = fileSizeToMb(fileSize);
  if (sizeInMB < 1) {
    const sizeInKB = fileSize / 1024;
    return `${sizeInKB.toFixed(2)} KB`;
  }
  return `${sizeInMB.toFixed(2)} MB`;
};

const Span2 = styled.span`
  text-align: left;
  font-size: 14px;
  margin-top: 4px;
`;

const UploadError = styled.span`
  text-align: left;
  font-size: 12px;
  color: #c82135;
`;

const CloseIcon = muiStyled(Close)({
  cursor: 'pointer',
});

type File = {
  imageFile: [{ name: string, size: number }]
}

interface Props {
  uploadSuccess: Boolean,
  file: File,
  handleClear: () => void,
  handleUploadClick: () => void,
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void,
  inputRef: React.MutableRefObject<HTMLInputElement | null>,
  uploadFileSizeError: Boolean,
  uploadFileTypeError: Boolean,
  uploadFileTemplateError: Boolean,
  fileUploading: Boolean,
  buttonText?: string,
  fileTypeErrorMessage?: string,
  acceptedFileTypes?: string,
  onCancelUpload?: () => void,
}

export default function UploadComponent({ uploadSuccess, fileUploading, file, handleUploadClick, handleClear, inputRef, handleFileChange,
  uploadFileSizeError,
  uploadFileTypeError,
  uploadFileTemplateError,
  buttonText = TEXT_LABELS.UPLOAD_PRODUCT_BROCHURE,
  fileTypeErrorMessage = TEXT_LABELS.FILE_FORMAT_XLXS,
  acceptedFileTypes,
  onCancelUpload
}: Props) {


  return (
    <FlexboxContainerComponent flexDirection="column" alignItems="center">
      <FlexboxContainerComponent
        justifyContent="center"
        margin="32px 0px 0px 0px"
      >
        <Button
          bgColor="#FFFFFF"
          color={
            uploadSuccess ||
              uploadFileSizeError ||
              uploadFileTypeError ||
              uploadFileTemplateError
              ? '#FFFFFF'
              : '#18222B'
          }
          borderColor="#666C73"
          padding="10px 0"
          width="320px"
          onClick={file.imageFile.length > 0 ? () => { } : handleUploadClick}
          cursor={file.imageFile.length > 0 ? 'initial' : 'pointer'}
          background={
            uploadSuccess
              ? '#18222B'
              : uploadFileSizeError ||
                uploadFileTypeError ||
                uploadFileTemplateError
                ? '#C82135'
                : '#FFFFFF'
          }
        >
          <div></div>
          <FlexboxContainerComponent width="100%">
            <div
              style={{
                width: file.imageFile.length > 0 ? '90%' : '100%',
                textAlign: 'center',
                alignSelf: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {file.imageFile.length > 0
                ? (() => {
                  const fileName = file.imageFile[0]?.name || '';
                  const maxLength = 25; // Maximum characters to display
                  if (fileName.length > maxLength) {
                    const extension = fileName.split('.').pop() || '';
                    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
                    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...';
                    return `${truncatedName}.${extension}`;
                  }
                  return fileName;
                })()
                : buttonText}
            </div>

            {file.imageFile.length > 0 ? (
              <FlexboxContainerComponent>
                <CloseIcon id="close-icon" onClick={handleClear} />{' '}
              </FlexboxContainerComponent>
            ) : (
              ''
            )}
          </FlexboxContainerComponent>
        </Button>
        <input
          type="file"
          ref={inputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept={acceptedFileTypes}
        />
      </FlexboxContainerComponent>
      <FlexboxContainerComponent width="320px">
        {uploadFileSizeError ? (
          <UploadError>
            {formatFileSize(file.imageFile[0]?.size || 0)}.
            {TEXT_LABELS.UPLOAD_SIZE_LESS_THAN_20}
          </UploadError>
        ) : uploadFileTypeError ? (
          <UploadError>{fileTypeErrorMessage}</UploadError>
        ) : uploadFileTemplateError ? (
          <UploadError>
            {TEXT_LABELS.WRONG_COLUMN_SET}
          </UploadError>
        ) : fileUploading &&
          !uploadFileSizeError &&
          !uploadFileTemplateError &&
          !uploadFileTypeError ? (
          <FlexboxContainerComponent flexDirection="column" alignItems="center" width="100%">
            <Span2>
              <Spinner color="black" size={14} /> {TEXT_LABELS.DONOT_CLOSE_WINDOW}
            </Span2>
            {onCancelUpload && (
              <Button
                bgColor="#C82135"
                color="#FFFFFF"
                borderColor="#C82135"
                padding="6px 16px"
                margin="8px 0 0 0"
                onClick={onCancelUpload}
                cursor="pointer"
              >
                Cancel Upload
              </Button>
            )}
          </FlexboxContainerComponent>
        ) : uploadSuccess ? (
          <Span2>
            {formatFileSize(file.imageFile[0]?.size || 0)}
          </Span2>
        ) : (
          <Span2>{TEXT_LABELS.UPTO_20_MB}</Span2>
        )}
      </FlexboxContainerComponent>
    </FlexboxContainerComponent>
  )

}